import { useRouter } from 'next/router';
import React, { Fragment, useContext, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get, capitalize } from 'lodash';
import URI from 'urijs';
import logger from 'Logger';
import { t } from 'Src/i18n';
import { FormFeedback } from 'Components/frameworks/form-feedback/FormFeedback';
import { Notification } from 'Components/frameworks/notification/Notification';
import { AsyncLoader } from 'Components/widgets/loading/async-loader/AsyncLoader';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { isIframePayment } from 'Utils/payment/payment.utils';
import { getDevice } from 'Views/account/pre-payment/PrePaymentView.queries';
import { useAppContext } from 'Context/ReactAppContext';
import { ICONS } from 'Components/utils/icon/icons';
import {
  WECHAT_PSP_ID,
  NODELCRERE_TRANSFER_PSP_ID,
  NODELCRERE_CHEQUE_PSP_ID,
  NAPAY_PSP_ID,
  PAYMENT_METHOD_LABEL,
} from 'Components/widgets/payment-info/PaymentInfo.const';
import { WeChatPayment } from 'Components/widgets/payment-info/payment-process/WeChatPayment';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { endLoading, startLoading } from 'Store/global-loader/globalLoader.action';
import { getCurrentProposal } from 'Store/booking/booking.selector';
import * as pleUtils from 'Utils/ple-utils';
import { IframePayment } from 'Components/widgets/payment-info/payment-process/IframePayment';
import { FormAutoSubmit } from 'Components/widgets/payment-info/payment-process/FormAutoSubmit';
import { usePrevious } from 'Src/hooks/use-previous/usePrevious';
import { redirectToOidc } from 'Utils/uri/redirectUtils';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { saveCustomerProposalAndSendContextToCxO } from 'Utils/seller/sellerUtils';
import { CXO_STEP } from 'Views/booking/constants';
import { useFormikContext } from 'formik';
import cookies from 'js-cookie';
import {
  VOXPAY_EMAIL,
  VOXPAY_SMS,
} from 'Components/widgets/payment-info/b2b-payment-info/VoxpayForm/VoxpayForm';
import { PaymentInfoContext } from './PaymentInfoContext';
import { getPaymentErrorMessagePath } from './PaymentInfo.utils';

const isSellerNoDelcrederePSP = (paymentProviderId) =>
  paymentProviderId === NODELCRERE_TRANSFER_PSP_ID ||
  paymentProviderId === NODELCRERE_CHEQUE_PSP_ID;

// Voxpay link payment exposes both an email and a phone input but only the channel
// the user picked must reach the API. Strip the other one from the payload without
// mutating Formik state, so what the user typed stays visible if they navigate back.
const buildBillingDetailsForSubmission = (billingDetails, selectedVoxpayMethod) => {
  if (!billingDetails || !selectedVoxpayMethod) {
    return billingDetails;
  }

  if (selectedVoxpayMethod === VOXPAY_SMS) {
    const { email, ...rest } = billingDetails;
    return rest;
  }

  if (selectedVoxpayMethod === VOXPAY_EMAIL) {
    const { mobilePhone, ...rest } = billingDetails;
    return rest;
  }

  const { email, mobilePhone, ...rest } = billingDetails;
  return rest;
};

type Props = {
  paymentConditionId: string;
  paymentProcessId: number;
  paymentMethod: PaymentMethod;
  amountToPay: number;
  onErrorCallback: () => void;
  shouldSkipPayment: boolean;
};
export const PaymentProcess = ({
  amountToPay,
  paymentConditionId,
  paymentProcessId,
  // just for trigger the re-fetch of data
  onErrorCallback,
  paymentMethod,
  shouldSkipPayment,
}: Props) => {
  const dispatch = useDispatch();
  const appContext = useAppContext();
  const responsive = useResponsive();
  const { isTablet } = responsive;
  const router = useRouter();
  const formik = useFormikContext();
  const {
    browserInfo,
    paymentProviderId,
    paymentId,
    token,
    billingDetails: rawBillingDetails,
    comments,
    currentPayingCustomerId: customerId,
    selectedVoxpayMethod,
  } = formik.values;
  const billingDetails = buildBillingDetailsForSubmission(rawBillingDetails, selectedVoxpayMethod);
  const { asPath, query: queryParams } = router;
  const salesmanID = useSelector((state) => state.seller.sellerInfo.salesmanID);
  const selectedProposal = useSelector(getCurrentProposal);
  const prevPaymentMethod = usePrevious(paymentMethod);
  const [errorPaymentMessageKey, setErrorPaymentMessageKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pspData, setPspData] = useState(null);
  const elementToScroll = useRef();
  const {
    trackPayment,
    paymentRedirectMutation,
    currency,
    onPaymentProgress,
    donationAmount,
    sellerOptionRedirect,
  } = useContext(PaymentInfoContext);
  const track = useTracking();
  const origin = new URI(appContext.originalUrl).origin();
  const location = new URI(origin + asPath);
  const isIframe = isIframePayment(paymentProviderId, appContext);
  const isExternalProvider = !isIframe && paymentProviderId !== WECHAT_PSP_ID;
  const explanatoryBillingInfoText = t('booking.banking.billingAdressFilling');
  const shouldShowBillingInfo = isIframe && explanatoryBillingInfoText;

  useEffect(() => {
    setErrorPaymentMessageKey(null);
  }, [paymentProviderId]);
  useEffect(() => {
    const fetchPaymentInfos = async () => {
      const { paymentRedirect } = await paymentRedirectMutation({
        amountToPay,
        browserInfo,
        paymentProviderId,
        paymentConditionId,
        isMobile: !isTablet,
        isIframe,
        device: getDevice(responsive),
        shouldSkipPayment,
        ...(shouldSkipPayment
          ? {
              comments,
            }
          : {}),
        token,
        donationAmount: donationAmount || null,
        location,
        billingDetails,
        callReference: appContext.callReference,
        contactID: appContext.contactID,
        customerId,
      });
      const paymentMethodLabel = paymentRedirect?.fields?.pp
        ? paymentRedirect.fields.pp.split(' ').map(capitalize).join(' ')
        : cookies.get(PAYMENT_METHOD_LABEL);
      trackPayment?.({
        orderId: paymentRedirect.orderId,
        bookingId: paymentRedirect.bookingId,
        amountToPay,
        paymentProviderId,
        paymentMethodLabel,
      });

      if (isExternalProvider && paymentRedirect.method === 'GET') {
        return window.location.replace(
          new URI(paymentRedirect.url).addQuery(paymentRedirect.fields).toString(),
        );
      }

      setPspData(paymentRedirect);
    };

    const continuePaymentProcess = async () => {
      try {
        if (!isLoading) {
          setIsLoading(true);
        }

        if (errorPaymentMessageKey) {
          setErrorPaymentMessageKey(null);
        }

        if (isExternalProvider) {
          dispatch(startLoading(t('booking.banking.loadingLabel')));
        }

        if (onPaymentProgress) {
          onPaymentProgress(location);
        }

        if (
          appContext.isCmta &&
          !appContext.cmta.isDelCredere &&
          isSellerNoDelcrederePSP(paymentProviderId)
        ) {
          await sellerOptionRedirect?.(paymentProviderId);
        } else if (paymentProviderId === NAPAY_PSP_ID) {
          return await saveCustomerProposalAndSendContextToCxO({
            proposalId: selectedProposal.id,
            step: CXO_STEP.PAYMENT,
            appContext,
            proposal: selectedProposal,
            queryParams,
            salesmanID,
            shouldSendDocument: false,
          });
        } else {
          !paymentId && (await fetchPaymentInfos());
        }
      } catch (error) {
        const errors = error.errors || [];
        trackPayment?.({
          orderId: null,
          isErrored: true,
          amountToPay,
          paymentProviderId,
          paymentMethodLabel: cookies.get(PAYMENT_METHOD_LABEL),
        });

        if (pleUtils.hasAuthResponseError(error)) {
          return redirectToOidc(appContext);
        }

        if (isExternalProvider) {
          dispatch(endLoading());
        }

        logger.error(error);
        onErrorCallback();
        setErrorPaymentMessageKey(
          getPaymentErrorMessagePath({
            errorCode: get(errors[0], 'errorCode'),
            track,
            isCmta: appContext.isCmta,
          }),
        );
      } finally {
        setIsLoading(false);
      }
    };

    continuePaymentProcess();

    if (elementToScroll.current) {
      scrollToElement(elementToScroll.current);
    }
  }, [paymentProcessId]);

  /**
   * if the paymentMethod change and there is still pspData we don't want to do anything and reset pspData
   * e.g. when the user for EOgone change from visa to paypal we don't want to redirect before the data for paypal are retrieved
   */
  if (prevPaymentMethod !== paymentMethod && pspData) {
    setPspData(null);
    return null;
  }

  if (errorPaymentMessageKey) {
    return (
      <div ref={elementToScroll}>
        <FormFeedback className="my-30" isError message={t(errorPaymentMessageKey)} />
      </div>
    );
  }

  // external psp with post redirect
  if (isExternalProvider) {
    if (!pspData) {
      if (paymentId) {
        return (
          <IframePayment paymentProviderId={paymentProviderId} paymentId={paymentId}>
            {null}
          </IframePayment>
        );
      }

      return null;
    }

    return <FormAutoSubmit pspData={pspData} />;
  }

  if (isLoading || !pspData) {
    return (
      <div
        className="relative flex h-[371px] w-full items-center justify-center bg-white"
        data-testid="PaymentProcess-loader"
        ref={elementToScroll}
      >
        <AsyncLoader />
      </div>
    );
  }

  return (
    <div className="mt-20" ref={elementToScroll}>
      {paymentProviderId === WECHAT_PSP_ID ? (
        <WeChatPayment
          currency={currency}
          qrCodeUrl={pspData.url}
          orderId={pspData.orderId}
          customerId={pspData.customerId}
          amountToPay={amountToPay}
          timeoutCallback={onErrorCallback}
        />
      ) : (
        <Fragment>
          {shouldShowBillingInfo && (
            <Notification
              className="my-10 w-full px-30 py-5 text-orange"
              iconName={ICONS.WARNING}
              iconWidth="1.3rem"
            >
              <p className="font-sans text-14 text-orange">{explanatoryBillingInfoText}</p>
            </Notification>
          )}
          {formik.isValid && (
            <IframePayment paymentProviderId={paymentProviderId}>
              <FormAutoSubmit pspData={pspData} />
            </IframePayment>
          )}
        </Fragment>
      )}
    </div>
  );
};
